import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../../../lib/utils';
import { mediumText, primaryText, secondaryText, semiBoldText } from '../../../../../styling/stylingHelper';
import { FrontendStaffUser, RESTAURANT_ROLES, updateStaffRole } from '../../../../../services/staff/staffService';
import { useStaffStore } from '../../../../../stores/appStaffStore';
import FilterDropdown from '../../../reports/hub/utilization/FilterDropdown';
import { SectionHeading, sectionPanelClass } from './sectionStyles';

interface StaffRoleSectionProps {
  user: FrontendStaffUser;
}

const StaffRoleSection: React.FC<StaffRoleSectionProps> = ({ user }) => {
  const { t } = useTranslation();
  const staffStore = useStaffStore();
  const [updatingRole, setUpdatingRole] = useState(false);

  const roleOptions = useMemo(() => RESTAURANT_ROLES.map((role) => ({ value: role, label: role })), []);

  const handleRoleChange = async (newRole: string) => {
    setUpdatingRole(true);
    try {
      await updateStaffRole(user.id, newRole);
      staffStore.updateStaffInStore(user.id, { restaurant_role: newRole });
    } catch (err) {
      console.error(err);
      alert(t('staff.updateRoleError'));
    } finally {
      setUpdatingRole(false);
    }
  };

  return (
    <section>
      <SectionHeading>{t('staff.role')}</SectionHeading>
      <div className={cn(sectionPanelClass, 'p-3 mt-3 sm:mt-4')}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className={cn('text-[14px] sm:text-[16px] leading-6 mb-1', primaryText, semiBoldText)}>
              {t('staff.restaurantRole')}
            </p>
            <p className={cn(secondaryText, mediumText, 'text-[14px] leading-5 sm:leading-6')}>
              {t('staff.restaurantRoleDescription')}
            </p>
          </div>
          <div className={cn(updatingRole && 'pointer-events-none opacity-60')}>
            <FilterDropdown
              label={t('staff.selectRole')}
              options={roleOptions}
              value={user.restaurant_role || ''}
              onChange={handleRoleChange}
              searchable
              alignRight
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default StaffRoleSection;
